import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Button } from 'chaoskit/src/components';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import { Icon } from '../../components';
import { IntroCopy } from './Step11';
import ReliabilityArticles from './ReliabilityArticles';
import {
  ArticleCTAWrapper,
  ArticleCTAButtonWrapper,
  ArticleCTAContentWrapper,
} from '../global/ArticleCTA';

const ReliabilityBreakdown = () => {
  const matrix = [
    [
      {
        grade: 'A+',
        range: '> 0.80',
      },
      {
        grade: 'A',
        range: '0.55 TO 0.79',
      },
      {
        grade: 'A-',
        range: '0.50 TO 0.54',
      },
    ],
    [
      {
        grade: 'B+',
        range: '0.40 TO 0.49',
      },
      {
        grade: 'B',
        range: '0.25 TO 0.39',
      },
      {
        grade: 'B-',
        range: '0.15 TO 0.24',
      },
    ],
    [
      {
        grade: 'C+',
        range: '0.12 TO 0.14',
      },
      {
        grade: 'C',
        range: '0.09 TO 0.11',
      },
      {
        grade: 'C-',
        range: '0.06 TO 0.08',
      },
      {
        grade: 'F',
        range: '0.0 TO 0.05',
      },
    ],
  ];

  return (
    <div className="reliabilityCalculator__gradeMatrix">
      {matrix.map((group, index) => {
        const key = `group-${index}`;

        return (
          <ul key={key} className="reliabilityCalculator__gradeMatrix-group">
            {group.map(item => (
              <li key={item.range}>
                <span className="u-textPrimary">{item.grade}</span>{' '}
                <span className="u-textMuted">=</span>
                <span>{item.range}</span>
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
};

const ReliabilityFAQ = ({ title, children, ...rest }) => (
  <li className="reliabilityCalculator__faq" {...rest}>
    {title && <h4 className="reliabilityCalculator__faq-question">{title}</h4>}
    <div className="reliabilityCalculator__faq-answer">{children}</div>
  </li>
);

ReliabilityFAQ.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const ReliabilityRecommendations = ({ grade, url }) => {
  const [urlCopied, setURLCopied] = useState(false);

  const handleUrlCopy = () => {
    setURLCopied(true);
  };

  useUpdateEffect(() => {
    if (urlCopied) {
      setTimeout(() => {
        setURLCopied(false);
      }, 2500);
    }
  }, [urlCopied]);

  return (
    <Fragment>
      <div className="reliabilityCalculator__recommended">
        <div className="reliabilityCalculator__recommended-letter">
          <span>{grade}</span>
          <h5 className="u-textRegular">Overall Grade</h5>
        </div>
        <div className="reliabilityCalculator__recommended-content u-contrast">
          <IntroCopy grade={grade} />

          <CopyToClipboard text={url} onCopy={handleUrlCopy}>
            <Button size="small" type="default" disabled={urlCopied}>
              {urlCopied ? (
                'Copied'
              ) : (
                <Fragment>
                  <Icon icon="copy" /> Copy Results URL
                </Fragment>
              )}
            </Button>
          </CopyToClipboard>
        </div>
      </div>

      <ReliabilityArticles />

      <ArticleCTAWrapper type="white" inline>
        <ArticleCTAContentWrapper>
          <h4>Build reliability through thoughtful chaos experiements.</h4>
          <p className="u-textNormal">
            With Gremlin Scenarios, you can easily reproduce outages to ensure
            you are resilient to similar failures in the future.
          </p>
        </ArticleCTAContentWrapper>
        <ArticleCTAButtonWrapper>
          <Button type="primary" url="/try-scenarios/">
            Get started free
          </Button>
        </ArticleCTAButtonWrapper>
      </ArticleCTAWrapper>

      <ul className="reliabilityCalculator__faq-list u-contrast">
        <ReliabilityFAQ title="How do we calculate your grade?">
          <p>
            We designed this calculator to help you prioritize efforts to make
            your different services more resilient. However, we also understand
            that no two services are alike and that our grades are just one of
            many factors you should consider when evaluating your service's
            reliability.
          </p>
          <p>
            We chose to consider the factors that we believe contribute most
            significantly to a service's reliability. We break these factors
            into 3 parts: criticality, availability, and ongoing maintenance
            work.
          </p>
        </ReliabilityFAQ>
        <ReliabilityFAQ title="Part 1: Criticality">
          <p>
            Represented as a number from 1 to 3, the criticality score measures
            how important the service is and therefore the standard to which it
            should be held. Simply put: customer-facing, mission-critical
            services have different reliability requirements than internal
            services.
          </p>
        </ReliabilityFAQ>
        <ReliabilityFAQ title="Part 2: Availability">
          <p>
            For teams that are already setting availability SLOs, this score is
            simply a measurement of how well they are achieving them. However,
            for teams that don't yet measure some of these KPIs, we calculate
            availability against a 5-nines availability SLO for Tier 1 services,
            4-nines for Tier 2, and 3-nines for Tier 3. The output of part 2 is
            a number between 0 and 1.
          </p>
        </ReliabilityFAQ>
        <ReliabilityFAQ title="Part 3: Ongoing Maintenance">
          <p>
            You accumulate points in part 3 for the various things you and your
            team do to ensure your service stays available. For example, having
            dedicated monitoring earns you 0.10 points, whereas dedicated
            monitoring and alerting earns you 0.25. The output of part 3 is a
            number between 0 and 1.
          </p>
          <p>
            To calculate your grade, we multiply your scores from parts 2 and 3
            together. For Tier 2 services, this is then multiplied by a factor
            of 1.3. For Tier 3 services, the product of your part 2 and 3 scores
            is multiplied by a factor of 1.7. The ranges below then determine
            your final grade.
          </p>
          <ReliabilityBreakdown />
        </ReliabilityFAQ>
        <ReliabilityFAQ>
          <p>
            Do you have feedback about our methodology? We'd love to hear it!
            Contact{' '}
            <a className="u-linkPrimary" href="mailto:peter@gremlin.com">
              peter@gremlin.com
            </a>{' '}
            to share your thoughts.
          </p>
        </ReliabilityFAQ>
      </ul>
    </Fragment>
  );
};

ReliabilityRecommendations.propTypes = {
  grade: PropTypes.string.isRequired,
  url: PropTypes.string,
};

export default ReliabilityRecommendations;
